// TB-072: shared helpers for the unauthenticated auth pages (all parties).
// Pure functions plus tiny storage wrappers; no API calls.

export const PARTIES = ['patient', 'doctor', 'establishment']

// Auth routes rendered by each layout (header + two-pane body).
export const PATIENT_AUTH_ROUTES = new Set([
  'login', 'signup', 'verification', 'signup-information',
  'forgot-password', 'forgot-password-verification', 'reset-new-password',
  'set-password' // TB-140
])

export const DOCTOR_AUTH_ROUTES = new Set([
  'doctor-login', 'doctor-signup', 'doctor-signup-information',
  'doctor-signup-information-establishment', 'doctor-signup-establishment',
  'doctor-verification', 'doctor-verification-establishment',
  'doctor-forgot-password', 'doctor-forgot-password-verification', 'doctor-reset-password',
  'doctor-set-password' // TB-081 (DEF-174)
])

// Entry routes: a user with a live session is sent to their dashboard.
// Mid-flow routes (verification, signup-information, reset) are left alone
// because the signup flow stores the token before the profile is complete.
export const ENTRY_AUTH_ROUTES = new Set([
  'login', 'signup', 'forgot-password',
  'doctor-login', 'doctor-signup', 'doctor-signup-establishment', 'doctor-forgot-password'
])

// Doctor-portal routes that are only ever establishment pages.
const ESTABLISHMENT_ROUTES = new Set([
  'doctor-signup-establishment', 'doctor-signup-information-establishment',
  'doctor-verification-establishment'
])

// Doctor-portal routes shared by doctors and establishments.
const SHARED_DOCTOR_ROUTES = new Set([
  'doctor-login', 'doctor-forgot-password', 'doctor-forgot-password-verification',
  'doctor-reset-password'
])

const HINT_KEY = 'tb-auth-party'

function readHint () {
  try { return sessionStorage.getItem(HINT_KEY) } catch (e) { return null }
}
function writeHint (val) {
  try {
    if (val) sessionStorage.setItem(HINT_KEY, val)
    else sessionStorage.removeItem(HINT_KEY)
  } catch (e) { /* storage blocked: fall back to the route default */ }
}

/**
 * Keep the per-tab establishment hint in step with navigation. Called from
 * the doctor layout (created + $route watcher), never from a computed.
 * - `?party=establishment|doctor` on a shared doctor page sets / clears it
 * - plain /doctor/login clears it (a fresh doctor entry)
 * - leaving the doctor auth pages clears it, so it never goes stale
 */
export function syncDoctorPartyHint (route) {
  const name = route && typeof route.name === 'string' ? route.name : ''
  if (!DOCTOR_AUTH_ROUTES.has(name)) { writeHint(null); return }
  if (ESTABLISHMENT_ROUTES.has(name)) { writeHint('establishment'); return }
  const q = route.query && route.query.party
  if (q === 'establishment') writeHint('establishment')
  else if (q === 'doctor' || name === 'doctor-login') writeHint(null)
}

/**
 * Party for a doctor-portal auth route (read-only). `?party=establishment`
 * on the shared pages (login, forgot, reset) shows the establishment pane;
 * later steps of that flow follow the hint written by syncDoctorPartyHint.
 */
export function resolveDoctorParty (route) {
  const name = route && typeof route.name === 'string' ? route.name : ''
  if (ESTABLISHMENT_ROUTES.has(name)) return 'establishment'
  if (!SHARED_DOCTOR_ROUTES.has(name)) return 'doctor'
  const q = route.query && route.query.party
  if (q === 'establishment' || q === 'doctor') return q
  if (name === 'doctor-login') return 'doctor'
  return readHint() === 'establishment' ? 'establishment' : 'doctor'
}

/** Doctor-portal login link that keeps the establishment context of this tab. */
export function doctorLoginLink () {
  return readHint() === 'establishment' ? { path: '/doctor/login', query: { party: 'establishment' } } : '/doctor/login'
}

/**
 * Public home for each party. There is no logged-out doctor landing in this
 * app (/doctor is the protected dashboard), so doctors and clinics go to
 * their portal sign-in, which is their public entry point.
 */
export function homeLinkFor (party) {
  if (party === 'establishment') return { path: '/doctor/login', query: { party: 'establishment' } }
  if (party === 'doctor') return '/doctor/login'
  return '/'
}

/** Log in / Sign up targets for the public header and mobile drawer. */
export function authLinksFor (party) {
  if (party === 'establishment') {
    return {
      login: { path: '/doctor/login', query: { party: 'establishment' } },
      signup: '/doctor/signup-establishment'
    }
  }
  if (party === 'doctor') return { login: '/doctor/login', signup: '/doctor/signup' }
  return { login: '/login', signup: '/signup' }
}

/** Which header button matches the current auth page (shown as active). */
export function activeAuthAction (route) {
  const name = route && typeof route.name === 'string' ? route.name : ''
  if (name === 'login' || name === 'doctor-login') return 'login'
  if (/signup/.test(name)) return 'signup'
  return null
}

// Same source as the main.js router guard ('user' first).
function readUser () {
  try {
    return JSON.parse(localStorage.getItem('user')) || JSON.parse(localStorage.getItem('userInfo'))
  } catch (e) { return null }
}

// UX only: decode the JWT payload's exp (no signature check). A malformed or
// expired token is treated as no session so the sign-in form stays reachable.
function tokenLooksLive (token) {
  try {
    const part = token.split('.')[1]
    if (!part) return false
    const json = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')))
    if (json && typeof json.exp === 'number') return json.exp * 1000 > Date.now()
    return true
  } catch (e) {
    return false
  }
}

/**
 * Dashboard path when this browser holds a session, else null.
 * This is a UX redirect based on locally stored keys (anyone can set them in
 * devtools); it gates nothing. The backend stays the authority, and a 401
 * clears the keys (axios.js).
 */
export function loggedInHome () {
  let token = null
  let flag = null
  try {
    token = localStorage.getItem('accessToken')
    flag = localStorage.getItem('_isLoggedIn')
  } catch (e) { return null }
  if (!token || !flag || flag === 'false' || !tokenLooksLive(token)) return null
  const user = readUser()
  const role = user ? user.userRole || user.role : null
  if (role === 'establishment') return '/doctor/establishment-doctor'
  if (role === 'provider' || role === 'estProvider') return '/doctor'
  return '/'
}

/** Redirect target if `route` is an auth entry route and a session exists. */
export function authRedirectFor (route) {
  const name = route && typeof route.name === 'string' ? route.name : ''
  if (!ENTRY_AUTH_ROUTES.has(name)) return null
  return loggedInHome()
}
